import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';

interface AuditRow {
  id: string;
  actorUserId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export default function AuditPage() {
  const query = useQuery({
    queryKey: ['audit'],
    queryFn: async () => {
      const { data } = await api.get<{ items: AuditRow[]; total: number }>('/audit', {
        params: { page: 1, pageSize: 100 },
      });
      return data;
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Auditoria</h1>
        <p className="text-sm text-text-muted">
          Log de eventos relevantes para segurança e operação.
        </p>
      </header>
      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Últimos 100 eventos</h2>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center text-text-muted">
              <Spinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-text-muted text-left">
                  <tr>
                    <th className="py-2">Quando</th>
                    <th>Ação</th>
                    <th>Ator</th>
                    <th>Alvo</th>
                    <th>IP</th>
                    <th>Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data?.items.map((e) => (
                    <tr key={e.id} className="border-t border-border align-top">
                      <td className="py-2 text-text-muted whitespace-nowrap">
                        {new Date(e.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td>
                        <Badge tone="info">{e.action}</Badge>
                      </td>
                      <td className="font-mono">{e.actorUserId ?? '—'}</td>
                      <td className="font-mono">
                        {e.targetType ? `${e.targetType}:${e.targetId}` : '—'}
                      </td>
                      <td className="font-mono">{e.ip ?? '—'}</td>
                      <td className="max-w-xs">
                        {e.metadata ? (
                          <code className="block whitespace-pre-wrap text-xs">
                            {JSON.stringify(e.metadata)}
                          </code>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
